/**
 * Collection Rate Card with window selector.
 *
 * Shows two related but distinct metrics for the selected window:
 *
 *   1. **Collection Rate** (primary) — strict effectiveness ratio. Of the
 *      dollars invoiced in this window, the percent paid against those
 *      specific invoices. Net of refund reversals, capped at 100%.
 *      Answers: "did we collect what we billed?"
 *
 *   2. **Cash Velocity** (secondary) — independent flow ratio. Payments
 *      received in this window over invoices issued in the same window.
 *      Can exceed 100% during catch-up on prior-period overdues.
 *      Answers: "how does cash flowing in compare to bills going out?"
 *
 * Both are real, useful, and answer different questions. Surfacing both
 * keeps the headline number honest (a 92% Collection Rate is the truth)
 * while preserving the cash-flow signal a finance owner cares about.
 */

import React, { useMemo, useState } from "react";
import {
  Box,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { TrendingUp as RateIcon } from "@mui/icons-material";
import { MetricSparkline } from "@/components/charts";
import { StatCardFrame, StatCardHead } from "./StatCard";

type RateWindow = "30d" | "mtd" | "ytd" | "all";

const STORAGE_KEY = "dashboard.collection_rate_window";

const isRateWindow = (v: unknown): v is RateWindow =>
  v === "30d" || v === "mtd" || v === "ytd" || v === "all";

const readStoredWindow = (): RateWindow => {
  // localStorage isn't available in SSR or sandboxed iframes — read defensively
  // and fall back to the 30-day default rather than crashing the dashboard.
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isRateWindow(raw) ? raw : "30d";
  } catch {
    return "30d";
  }
};

const writeStoredWindow = (w: RateWindow): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, w);
  } catch {
    // Quota exceeded / disabled storage — silently ignore; the in-memory
    // state still works for the current session.
  }
};

interface SparklineDataPoint {
  value: number;
  label?: string;
}

interface CollectionRateCardProps {
  /** Strict collection rate per window. */
  rate30d?: number;
  rateMtd?: number;
  rateYtd?: number;
  rateAll: number;
  /** Cash velocity per window (independent flow ratio). */
  velocity30d?: number;
  velocityMtd?: number;
  velocityYtd?: number;
  onClick?: () => void;
  sparklineData?: SparklineDataPoint[];
}

const WINDOW_LABELS: Record<RateWindow, { chip: string; subtitle: string }> = {
  "30d": { chip: "30D", subtitle: "Last 30 days" },
  mtd: { chip: "MTD", subtitle: "Month to date" },
  ytd: { chip: "YTD", subtitle: "Year to date" },
  all: { chip: "All", subtitle: "All time" },
};

// Exported for direct unit-testing of the threshold mapping. The card
// only consumes the result internally, but the green/yellow/red bands
// are user-visible and easy to break with a stray edit; pinning them
// in a pure-function test catches regressions the render-path tests
// would miss in jsdom.
export const getRateColor = (rate: number): "success" | "warning" | "error" => {
  if (rate >= 75) return "success";
  if (rate >= 50) return "warning";
  return "error";
};

const TOOLTIP_BODY = (
  <Box sx={{ p: 0.5, maxWidth: 280 }}>
    <Typography variant="caption" sx={{ display: "block", fontWeight: 600, mb: 0.5 }}>
      Collection Rate
    </Typography>
    <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
      Of the dollars invoiced in this window, the percent paid against those
      specific invoices. Net of refunds, capped at 100%.
    </Typography>
    <Typography variant="caption" sx={{ display: "block", fontWeight: 600, mb: 0.5 }}>
      Cash Velocity
    </Typography>
    <Typography variant="caption" sx={{ display: "block" }}>
      Payments received vs invoices issued in this window. Can go above 100%
      when families catch up on prior-period overdues — a healthy cash-flow
      signal, not a collection-effectiveness metric.
    </Typography>
  </Box>
);

export const CollectionRateCard: React.FC<CollectionRateCardProps> = ({
  rate30d,
  rateMtd,
  rateYtd,
  rateAll,
  velocity30d,
  velocityMtd,
  velocityYtd,
  onClick,
  sparklineData,
}) => {
  const [selectedWindow, setSelectedWindow] = useState<RateWindow>(readStoredWindow);

  const rate = useMemo(() => {
    switch (selectedWindow) {
      case "30d":
        return rate30d ?? rateAll;
      case "mtd":
        return rateMtd ?? rateAll;
      case "ytd":
        return rateYtd ?? rateAll;
      case "all":
      default:
        return rateAll;
    }
  }, [selectedWindow, rate30d, rateMtd, rateYtd, rateAll]);

  const velocity = useMemo(() => {
    switch (selectedWindow) {
      case "30d":
        return velocity30d;
      case "mtd":
        return velocityMtd;
      case "ytd":
        return velocityYtd;
      case "all":
      default:
        // No "all-time" cash velocity — the metric only makes sense
        // against an issued-window denominator.
        return undefined;
    }
  }, [selectedWindow, velocity30d, velocityMtd, velocityYtd]);

  const color = getRateColor(rate);

  return (
    <StatCardFrame onClick={onClick}>
      <CardContent
        sx={{
          p: 2.25,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 2.25 },
        }}
      >
        <StatCardHead
          label="Collection Rate"
          tooltip={TOOLTIP_BODY}
          icon={<RateIcon />}
          color={color}
          value={`${rate.toFixed(1)}%`}
          valueColor={`${color}.main`}
          subtitle={
            <>
              {WINDOW_LABELS[selectedWindow].subtitle} · paid against invoices
              issued
              {velocity !== undefined && (
                <Box component="span" sx={{ display: "block", mt: 0.25 }}>
                  Cash velocity {velocity.toFixed(1)}%
                </Box>
              )}
            </>
          }
        />

        {sparklineData && sparklineData.length > 1 && (
          <Box sx={{ mt: 1.5 }}>
            <MetricSparkline
              data={sparklineData}
              height={34}
              color={color}
              showTrend={false}
              formatValue={(v) => `${v}%`}
            />
          </Box>
        )}

        <ToggleButtonGroup
          size="small"
          value={selectedWindow}
          exclusive
          onChange={(e, v: RateWindow | null) => {
            e.stopPropagation();
            if (v) {
              setSelectedWindow(v);
              writeStoredWindow(v);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            mt: 1.5,
            "& .MuiToggleButton-root": {
              fontSize: "0.6rem",
              px: 0.75,
              py: 0.25,
              lineHeight: 1.2,
              textTransform: "none",
              border: "1px solid",
              borderColor: "divider",
            },
          }}
          aria-label="collection rate window"
        >
          {(Object.keys(WINDOW_LABELS) as RateWindow[]).map((w) => (
            <ToggleButton key={w} value={w} aria-label={`${WINDOW_LABELS[w].chip}, ${WINDOW_LABELS[w].subtitle}`}>
              {WINDOW_LABELS[w].chip}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </CardContent>
    </StatCardFrame>
  );
};
